import { z } from 'zod';

export type WordStatus = 'NEW' | 'FUZZY' | 'MASTERED';

export interface VocabItem {
  word: string;
  translation: string;
  phonetic?: string;
  example?: string;
  source: 'youdao' | 'original';
  explains?: string[];
  savedAt: number;
  addedAt?: number;
  status: WordStatus;
  lastReviewedAt: number | null;
  nextReviewAt: number | null;
  stageIndex: number;
  bootstrapDone: boolean;
  ease: number;
  interval: number;
  reps: number;
  correctStreak: number;
  wrongStreak: number;
  episodeId?: string;
  episodeTitle?: string;
  contextText?: string;
  episodeThumbnail?: string;
}

export const DAY = 24 * 60 * 60 * 1000;

export const DEFAULT_INTERVALS: Record<WordStatus, number[]> = {
  NEW: [0, 1, 3],
  FUZZY: [0, 2, 5],
  MASTERED: [30, 60, 120],
};

const MINIMUM_DELAY = 10 * 60 * 1000; // 10 minutes fallback to avoid instant repeats

export const vocabItemSchema = z.object({
  word: z.string(),
  translation: z.string(),
  phonetic: z.string().optional(),
  example: z.string().optional(),
  source: z.union([z.literal('youdao'), z.literal('original')]),
  explains: z.array(z.string()).optional(),
  savedAt: z.number(),
  addedAt: z.number().optional(),
  status: z.union([z.literal('NEW'), z.literal('FUZZY'), z.literal('MASTERED')]).default('NEW'),
  lastReviewedAt: z.number().nullable().default(null),
  nextReviewAt: z.number().nullable().default(null),
  stageIndex: z.number().int().default(0),
  bootstrapDone: z.boolean().default(false),
  ease: z.number().default(2.5),
  interval: z.number().default(0),
  reps: z.number().int().default(0),
  correctStreak: z.number().int().default(0),
  wrongStreak: z.number().int().default(0),
  episodeId: z.string().optional(),
  episodeTitle: z.string().optional(),
  contextText: z.string().optional(),
  episodeThumbnail: z.string().optional(),
});

export function scheduleByBucket(word: VocabItem, now = Date.now()): number {
  const plan = DEFAULT_INTERVALS[word.status] || [3];
  const idx = Math.min(Math.max(word.stageIndex || 0, 0), plan.length - 1);
  return now + plan[idx] * DAY;
}

export function scheduleBySM2(word: VocabItem, quality: number, now = Date.now()): number {
  let { reps = 0, ease = 2.5, interval = 0 } = word;

  if (quality < 3) {
    reps = 0;
    interval = 1;
  } else {
    reps += 1;
    if (reps === 1) interval = 1;
    else if (reps === 2) interval = 3;
    else interval = Math.round(Math.max(1, interval) * ease);

    ease = ease + (0.1 - (5 - quality) * (0.08 + (5 - quality) * 0.02));
    ease = Math.max(1.3, ease);
  }

  word.reps = reps;
  word.ease = ease;
  word.interval = interval;
  return now + interval * DAY;
}

export function reviewAndReschedule(word: VocabItem, quality: number, now = Date.now()): VocabItem {
  const next = { ...word };
  next.lastReviewedAt = now;

  if (quality >= 4) {
    next.correctStreak = (next.correctStreak || 0) + 1;
    next.wrongStreak = 0;
    if (next.correctStreak >= 2) {
      if (next.status === 'NEW') next.status = 'FUZZY';
      else if (next.status === 'FUZZY') next.status = 'MASTERED';
      next.correctStreak = 0;
    }
  } else if (quality <= 2) {
    next.wrongStreak = (next.wrongStreak || 0) + 1;
    next.correctStreak = 0;
    if (next.status === 'MASTERED' && next.wrongStreak >= 1) next.status = 'FUZZY';
    else if (next.status === 'FUZZY' && next.wrongStreak >= 2) next.status = 'NEW';
  } else {
    next.correctStreak = 0;
    next.wrongStreak = 0;
  }

  if (!next.bootstrapDone) {
    const plan = DEFAULT_INTERVALS[next.status] || [1];
    const planMaxIndex = Math.max(plan.length - 1, 0);
    let stage = Math.max(Math.min(next.stageIndex || 0, planMaxIndex), 0);

    if (quality >= 4 && stage < planMaxIndex) stage += 1;
    if (quality <= 2 && stage > 0) stage -= 1;

    next.stageIndex = stage;
    next.nextReviewAt = scheduleByBucket(next, now);

    if (stage >= planMaxIndex) {
      next.bootstrapDone = true;
      next.reps = Math.max(1, next.reps || 1);
      next.interval = Math.max(1, next.interval || 1);
    }
  }

  if (next.bootstrapDone) {
    next.nextReviewAt = scheduleBySM2(next, quality, now);
  }

  if (!next.nextReviewAt || next.nextReviewAt <= now) {
    next.nextReviewAt = now + MINIMUM_DELAY;
  }

  return next;
}

export function dueBy(words: VocabItem[], until = Date.now()): VocabItem[] {
  return words
    .filter((w) => w.nextReviewAt != null && w.nextReviewAt <= until)
    .sort((a, b) => (a.nextReviewAt! - b.nextReviewAt!));
}

export function buildPlan(words: VocabItem[], days = 7, now = Date.now()): { date: string; items: VocabItem[] }[] {
  const buckets = Array.from({ length: days }, (_, idx) => ({
    date: new Date(now + idx * DAY).toISOString().slice(0, 10),
    items: [] as VocabItem[],
  }));

  for (const w of words) {
    const item = { ...w };
    if (item.nextReviewAt == null) {
      item.nextReviewAt = scheduleByBucket(item, now);
    }
    const delta = Math.floor((item.nextReviewAt - now) / DAY);
    if (delta >= 0 && delta < buckets.length) {
      buckets[delta].items.push(item);
    }
  }

  return buckets;
}

export function normalizeWord(raw: VocabItem): VocabItem {
  return vocabItemSchema.parse(raw);
}

export function applyReviewQuality(word: VocabItem, quality: number, now = Date.now()): VocabItem {
    const normalized = normalizeWord(word);
    return reviewAndReschedule(normalized, quality, now);
}
